import {
  cloneElement,
  CSSProperties,
  isValidElement,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react"
import { definition, styles } from "@uesio/ui"
import {
  useFloating,
  useInteractions,
  arrow,
  flip,
  offset,
  shift,
  useHover,
  Placement,
  FloatingPortal,
  FloatingArrow,
  safePolygon,
} from "@floating-ui/react"

interface TooltipUtilityProps {
  text: string
  placement?: Placement
  offset?: number
  instant?: boolean
  hideArrow?: boolean
  maxWidth?: number | string
  contentStyle?: CSSProperties
}

const StyleDefaults = Object.freeze({
  tooltip: [
    "px-2",
    "py-1.5",
    "bg-slate-800",
    "text-slate-100",
    "z-10",
    "text-xs",
    "rounded",
  ],
  arrow: ["fill-slate-800"],
})

const Tooltip: definition.UtilityComponent<TooltipUtilityProps> = (props) => {
  const { children } = props
  const arrowRef = useRef(null)
  const [open, setOpen] = useState<boolean>(false)
  const [referenceEl, setReferenceEl] = useState<Element | null>(null)
  const middleware = useMemo(
    () => [
      offset(props.offset ?? 8),
      flip({
        padding: 8,
      }),
      shift({
        padding: 8,
      }),
      ...(props.hideArrow
        ? []
        : [
            // eslint-disable-next-line react-hooks/refs -- False positive per https://github.com/floating-ui/floating-ui/discussions/3405 & https://github.com/facebook/react/issues/34775
            arrow({
              element: arrowRef,
            }),
          ]),
    ],
    [props.offset, props.hideArrow, arrowRef],
  )

  const { x, y, strategy, refs, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: props.placement,
    middleware,
  })

  const { setReference, setFloating } = refs
  const setReferenceCallback = useCallback(
    (node: Element | null) => {
      setReference(node)
      setReferenceEl(node)
    },
    [setReference],
  )
  const floatingRef = useCallback(
    (node: HTMLElement | null) => {
      setFloating(node)
    },
    [setFloating],
  )

  const hover = useHover(context, {
    move: false,
    restMs: props.instant ? 0 : 400,
    delay: props.instant ? { open: 0, close: 0 } : { open: 1000 },
    handleClose: safePolygon(),
  })
  const { getReferenceProps, getFloatingProps } = useInteractions([hover])

  const classes = styles.useUtilityStyleTokens(StyleDefaults, props)

  return (
    <>
      {isValidElement(children) &&
        cloneElement(
          children,
          getReferenceProps({
            ref: setReferenceCallback,
            ...(children.props || {}),
          }),
        )}
      {open && (
        <FloatingPortal root={styles.getClosestThemeRoot(referenceEl)}>
          {
            <div
              {...getFloatingProps()}
              className={classes.tooltip}
              ref={floatingRef}
              style={{
                position: strategy,
                top: y ?? 0,
                left: x ?? 0,
                width: "max-content",
                maxWidth: props.maxWidth,
                ...props.contentStyle,
              }}
            >
              {props.text}
              {!props.hideArrow && (
                <FloatingArrow
                  width={12}
                  height={6}
                  className={classes.arrow}
                  ref={arrowRef}
                  context={context}
                />
              )}
            </div>
          }
        </FloatingPortal>
      )}
    </>
  )
}

export default Tooltip
